import { useEffect, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ExternalLinkIcon, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import Spinner from "../../../assets/icons/Spinner";
import Edit2Icon from "../../../assets/icons/Edit2Icon";
import CheckIcon from "../../../assets/icons/CheckIcon";
import label from "../../../i18n/label";
import Message from "../../../i18n/Message";
import backend from "../../../backend/backend";
import {
  loadTemplate,
  instantiateTemplate,
  clearTemplate,
  deleteTemplate,
} from "../store/builderActionCreators";
import { builderSelectors } from "../store/builderReducer";
import BuilderActions from "../store/builderActions";
import NewWorkspaceLayout from "../../workspace/layout/NewWorkspaceLayout";
import { linkToBuilder, linkToTemplates, linkToBuilderProject } from "../../../routes";
import "./TemplateDetailPage.scss";

dayjs.extend(localizedFormat);

const TemplateDetailPage = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const nav = useNavigate();
  const { workspaceId, templateUid } = useParams();

  const template = useSelector(builderSelectors.getCurrentTemplate);
  const loading = useSelector(builderSelectors.isCurrentTemplateLoading);
  const instantiating = useSelector(builderSelectors.isTemplateInstantiating);
  const deletingTemplateUid = useSelector(builderSelectors.getDeletingTemplateUid);

  // Name editing state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isDeleting = deletingTemplateUid === templateUid;

  useEffect(() => {
    if (templateUid) {
      dispatch(loadTemplate(templateUid));
    }
    return () => {
      dispatch(clearTemplate());
    };
  }, [dispatch, templateUid]);

  const handleStartWithTemplate = async () => {
    try {
      const result = await dispatch(instantiateTemplate(intl, workspaceId, templateUid));
      if (result && result.chatUid) {
        nav(linkToBuilderProject(workspaceId, result.chatUid));
      }
    } catch (err) {
      // Error already handled in action creator
    }
  };

  // Use preview URL from backend (includes correct scheme and domain)
  const previewUrl = template?.previewUrl || null;

  // Handle open in new tab
  const handleOpenNewTab = useCallback(() => {
    if (previewUrl) {
      window.open(previewUrl, "_blank");
    }
  }, [previewUrl]);

  // Name editing handlers
  const startEditingName = useCallback(() => {
    setNameValue(template?.name || "");
    setEditingName(true);
  }, [template?.name]);

  const saveName = useCallback(async (e) => {
    e.preventDefault();
    if (!nameValue.trim()) return;

    setSavingName(true);
    try {
      const updatedTemplate = await backend.updateTemplate(templateUid, { name: nameValue.trim() });
      dispatch({
        type: BuilderActions.TEMPLATE_LOADED,
        template: updatedTemplate,
      });
      setEditingName(false);
    } catch (err) {
      console.error("Failed to save template name:", err);
    } finally {
      setSavingName(false);
    }
  }, [templateUid, nameValue, dispatch]);

  const handleNameBlur = useCallback((e) => {
    // Don't trigger save if clicking the save button
    if (e.relatedTarget?.type === "submit") return;
    if (nameValue.trim()) {
      saveName(e);
    } else {
      setEditingName(false);
    }
  }, [nameValue, saveName]);

  // Delete handler
  const onDeleteConfirmed = useCallback(() => {
    dispatch(
      deleteTemplate(intl, templateUid, () => {
        setShowDeleteConfirm(false);
        nav(linkToTemplates(workspaceId));
      })
    );
  }, [dispatch, intl, templateUid, workspaceId, nav]);

  if (loading || !template) {
    return (
      <NewWorkspaceLayout contentPanelClassName="template_detail_page">
        <div className="loading_container">
          <Spinner />
        </div>
      </NewWorkspaceLayout>
    );
  }

  return (
    <NewWorkspaceLayout contentPanelClassName="template_detail_page">
      <div className="breadcrumbs">
        <Link to={linkToBuilder(workspaceId)}>
          <Message id="app_builder_projects_title" />
        </Link>
        <span className="separator">/</span>
        <Link to={linkToTemplates(workspaceId)}>
          <Message id="template_catalogue" />
        </Link>
        <span className="separator">/</span>
        <span className="current">{template.name}</span>
      </div>

      <div className="template_detail_content">
        <div className="preview_panel">
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title={template.name}
              className="preview_iframe"
              sandbox="allow-scripts allow-same-origin"
            />
          ) : (
            <div className="preview_placeholder">
              {template.screenshotUrl ? (
                <img
                  src={template.screenshotUrl}
                  alt={template.name}
                  className="placeholder_image"
                />
              ) : (
                <span className="no_preview">
                  <Message id="preview_not_available" />
                </span>
              )}
            </div>
          )}
        </div>

        <div className="info_panel">
          {template.isOwner ? (
            <form className="template_name_form" onSubmit={saveName}>
              {!editingName ? (
                <h1 className="template_name clickable" onClick={startEditingName}>
                  {template.name}
                  <Edit2Icon size="16" color="#7b809a" />
                </h1>
              ) : (
                <div className="name_edit_container">
                  <input
                    type="text"
                    className="template_name_input"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onBlur={handleNameBlur}
                    autoFocus
                    required
                  />
                  <button type="submit" className="save_name_btn" disabled={savingName}>
                    {savingName ? <Spinner size={16} /> : <CheckIcon size="16" color="#28a745" />}
                  </button>
                </div>
              )}
            </form>
          ) : (
            <h1 className="template_name">{template.name}</h1>
          )}

          {template.isGlobal && (
            <span className="category_badge">Global</span>
          )}

          <div className="meta_info">
            {template.authorName && (
              <div className="meta_item">
                <span className="meta_label"><Message id="author" /></span>
                <span className="meta_value">{template.authorName}</span>
              </div>
            )}
            {template.dateCreated && (
              <div className="meta_item">
                <span className="meta_label"><Message id="created_on" /></span>
                <span className="meta_value">{dayjs(template.dateCreated).format("LL")}</span>
              </div>
            )}
          </div>

          {template.modules && template.modules.length > 0 && (
            <div className="technologies_section">
              <h3>
                <Message id="modules" defaultMessage="Modules" />
              </h3>
              <div className="tech_chips">
                {template.modules.map((mod) => (
                  <span key={mod} className="tech_chip">
                    {mod}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="action_buttons">
            <button
              className="btn btn-primary start_button"
              onClick={handleStartWithTemplate}
              disabled={instantiating}
            >
              {instantiating ? (
                <Spinner size={16} />
              ) : (
                <Message id="start_with_this_template" />
              )}
            </button>
            {previewUrl && (
              <button
                type="button"
                className="open_preview_btn"
                onClick={handleOpenNewTab}
                title={label(intl, "builder_preview_open_new_tab", "Open preview in a new tab")}
              >
                <ExternalLinkIcon size={18} />
              </button>
            )}
            {template.isOwner && (
              <div className="delete_template_container">
                <button
                  type="button"
                  className="delete_template_btn"
                  onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                  disabled={isDeleting}
                >
                  <Trash2 size={18} />
                </button>
                {showDeleteConfirm && (
                  <>
                    <div
                      className="cover"
                      onClick={() => setShowDeleteConfirm(false)}
                    />
                    <div className="confirm_panel">
                      <button
                        className="btn bg-gradient-primary btn-sm"
                        onClick={onDeleteConfirmed}
                        disabled={isDeleting}
                      >
                        {isDeleting ? <Spinner size={16} /> : <Message id="confirm_delete" />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </NewWorkspaceLayout>
  );
};

export default TemplateDetailPage;
